// ─── Aritmética de fechas para calendarios de pago ───────────────────────────
//
// 🕐 ESTE ARCHIVO ES EL ÚNICO DUEÑO DE LA HORA DE NEGOCIO (6-oct, Mau: «tiene que haber un solo lugar que calcule y marque la hora, o sea el motor»):
//    «¿qué día es hoy en Mérida?», «hace/dentro de N días», la semana de trabajo, el rango de un año en instantes UTC y la zona horaria. Nadie más
//    calcula «hoy»: ni `new Date().toISOString()`, ni un `-6 horas`, ni `date-fns-tz`, ni `CURDATE()/NOW()` de MySQL (sesión en UTC).
//
// 🔴 ARCHIVO ESPEJO. Existe DOS veces, byte a byte idéntico:
//      backend/src/helpers/fechas.ts      ← lo usan routes, services y crons
//      packages/shared/types/fechas.ts    ← lo usa el frontend vía '@erp/shared'
//    No se puede tener uno solo: el tsconfig del backend tiene `rootDir: ./src`
//    y no compila nada de fuera de src/. Es el mismo patrón que ya usa la casa
//    para el diccionario de ENUMs (CLAUDE.md §25.1).
//    `fechas-una-sola-cuenta.test.ts` falla si los dos archivos divergen.
//    Si editas uno, copia el otro. No los "adaptes" por separado.
//
// BUG-126: `d.setUTCMonth(d.getUTCMonth() + n)` desborda cuando el día de origen no
// existe en el mes destino (31 de enero + 1 mes → 3 de marzo). En la ruta del dinero
// eso se traduce en cuotas que se brincan un mes, dos cuotas en el mismo mes, y CR01
// calculando mora sobre fechas que no corresponden.
//
// Regla (decisión Mau 2026-08-10): anclar al último día del mes cuando el día de
// origen no existe en el mes destino.

/** Último día del mes (1..31) para un año/mes dados. `mes` es 0-indexado. */
function ultimoDiaDelMes(anio: number, mes: number): number {
  // Día 0 del mes siguiente = último día de este mes. Resuelve bisiestos sin tabla.
  return new Date(Date.UTC(anio, mes + 1, 0)).getUTCDate();
}

/**
 * Suma `meses` a `base` anclando al último día del mes cuando el día original no
 * existe en el destino. Nunca desborda al mes siguiente.
 *
 *   31 ene + 1 → 28 feb (29 en bisiesto)
 *   31 ene + 2 → 31 mar   ← el día original se recupera; el anclaje NO se acumula
 *   15 may + 1 → 15 jun
 *
 * Conserva la hora UTC de `base` y no muta el argumento.
 *
 * Nota (diseño blindado 2026-09-01): `Date.UTC()`/`getUTC*` aquí son aritmética de
 * calendario deliberada sobre una fecha YA conocida — no deciden "hoy", no confundir
 * con `hoyEnMeridaISO()` (más abajo, en este mismo archivo), que sí lo decide.
 */
export function sumarMeses(base: Date, meses: number): Date {
  const anio = base.getUTCFullYear();
  const mes  = base.getUTCMonth();
  const dia  = base.getUTCDate();

  // El anclaje se calcula SIEMPRE contra la fecha base, nunca encadenando resultados:
  // así 31 ene + 2 devuelve 31 mar y no 28 mar (que es lo que daría acumular el ancla).
  const mesDestino  = mes + meses;
  const anioDestino = anio + Math.floor(mesDestino / 12);
  const mesNorm     = ((mesDestino % 12) + 12) % 12;

  const diaDestino = Math.min(dia, ultimoDiaDelMes(anioDestino, mesNorm));

  return new Date(Date.UTC(
    anioDestino, mesNorm, diaDestino,
    base.getUTCHours(), base.getUTCMinutes(), base.getUTCSeconds(), base.getUTCMilliseconds(),
  ));
}

/**
 * Devuelve `fecha` movida al día `dia` de SU mes, recortando al último día si ese mes
 * no lo tiene (31 sobre febrero → 28/29).
 *
 * BUG-132: los egresos recurrentes avanzaban desde `proxima_fecha`, no desde el día
 * pactado. Con `sumarMeses` ya no se saltan meses (BUG-128), pero una vez que la fecha
 * caía en un mes corto el día se quedaba pegado ahí para siempre:
 *   31 ene → 28 feb → 28 mar → 28 abr …
 * Anclando al día original: 31 ene → 28 feb → 31 mar → 30 abr.
 */
export function conDiaDelMes(fecha: Date, dia: number): Date {
  const anio = fecha.getUTCFullYear();
  const mes  = fecha.getUTCMonth();
  const diaReal = Math.min(Math.max(1, Math.trunc(dia)), ultimoDiaDelMes(anio, mes));
  return new Date(Date.UTC(
    anio, mes, diaReal,
    fecha.getUTCHours(), fecha.getUTCMinutes(), fecha.getUTCSeconds(), fecha.getUTCMilliseconds(),
  ));
}

/**
 * La misma cuenta que `sumarMeses`, pero de 'AAAA-MM-DD' a 'AAAA-MM-DD'.
 *
 * Es la puerta que usan los CUATRO cotizadores —el panel del ERP, el de pantalla
 * completa, el del backend y el del iframe que ve el broker (`frontend/cotizador/`)—,
 * que manejan la fecha como texto y NO como `Date`. Existe para que nadie tenga que
 * volver a escribir la conversión: hacerlo a mano fue justo el origen de los cálculos
 * paralelos que este helper vino a matar.
 * ⚠️ El cotizador del portal broker (`CotizadorBrokerForm.tsx`) NO entra en la lista:
 * calcula montos y ninguna fecha.
 *
 * 🔴 Todo el cálculo es en UTC a propósito. La versión anterior del cotizador del
 * backend construía la fecha con `Date.UTC(...)` y luego la leía con `getFullYear()`
 * / `getMonth()`, que son LOCALES: en cualquier huso al oeste de Greenwich —el de
 * Mérida, sin ir más lejos— la fecha retrocedía un mes entero y la mensualidad 1
 * caía ANTES del enganche. Medido el 14-ago: ancla 31-jul daba `2026-07-30`.
 */
export function sumarMesesISO(isoDate: string, meses: number): string {
  const [anio, mes, dia] = isoDate.split('-').map(Number);
  if (!anio || !mes || !dia) {
    throw new Error(`sumarMesesISO: fecha inválida «${isoDate}» (se espera AAAA-MM-DD)`);
  }
  // 🔴 Y la fecha tiene que EXISTIR en el calendario, no sólo tener la forma.
  // Sin esto, «2026-02-31» pasaba derecho y `Date.UTC` la desbordaba a marzo:
  // la primera mensualidad salía «2026-04-03». Es el MISMO desbordamiento que este
  // helper vino a matar, sólo que movido a la puerta de entrada — y es alcanzable
  // desde la API, porque el zod de las cotizaciones valida la fecha con un regex
  // `\d{4}-\d{2}-\d{2}`, que no sabe cuántos días tiene febrero.
  if (mes < 1 || mes > 12 || dia < 1 || dia > ultimoDiaDelMes(anio, mes - 1)) {
    throw new Error(
      `sumarMesesISO: «${isoDate}» no existe en el calendario ` +
      `(${mes < 1 || mes > 12 ? 'mes fuera de rango' : `${mes === 2 ? 'febrero' : 'ese mes'} no tiene ${dia} días`})`,
    );
  }
  // Mediodía UTC, no medianoche: deja 12 h de colchón por si alguien formatea la
  // fecha en horario local aguas abajo y la empuja al día anterior.
  const resultado = sumarMeses(new Date(Date.UTC(anio, mes - 1, dia, 12, 0, 0)), meses);
  return resultado.toISOString().slice(0, 10);
}

/**
 * Cuántos MESES CUMPLIDOS hay entre dos fechas ISO. Es la inversa de `sumarMesesISO`, y vive
 * pegada a ella a propósito: si una cambia, la otra tiene que cambiar en el mismo renglón.
 *
 * 🔴 POR QUÉ EXISTE Y QUÉ DECIDE. La usa la corrida del cotizador para saber en qué renglón
 * del calendario cae cada exhibición del enganche (Mig 53). Si contara mal, un pago del
 * cliente aparecería en un mes que no es — o, peor, en ninguno.
 *
 * **Cuenta meses CUMPLIDOS, no diferencia de calendario**: del 15-ene al 14-feb hay 0 meses,
 * no 1. Es lo que la hace la inversa exacta: `mesesEntreISO(f, sumarMesesISO(f, n)) === n`,
 * y eso está PROBADO en `fechas-una-sola-cuenta.test.ts` sobre 9 anclas × 24 meses — no
 * afirmado de palabra, que es como estuvo mal cuatro horas (BUG-463).
 *
 * 🔴 EL RECORTE DE FIN DE MES ES LA MITAD DE ESTA FUNCIÓN, NO UN CASO BORDE.
 * `sumarMesesISO` recorta al último día cuando el mes destino es más corto (31-ene + 1 mes
 * = 28-feb, decisión de Mau del 10-ago). La primera versión de esta función leía ese 28 < 31
 * como «el mes no se cumplió» y restaba uno ⇒ **fallaba en 40 de 125 pares**: una exhibición
 * del enganche fechada el 28-feb aparecía en el renglón del mes 0, pegada al enganche
 * inicial, y el papel del cliente saltaba 0 → 0 → 2 repitiendo fechas.
 * Por eso el día tope de un mes corto cuenta como cumplido: es el recorte de su hermana,
 * no un pago adelantado.
 *
 * 🔴 Todo en UTC, por la misma razón que su hermana: leer una fecha construida en UTC con
 * `getMonth()` local retrocede un mes entero en el huso de Mérida.
 */
export function mesesEntreISO(desde: string, hasta: string): number {
  const [a1, m1, d1] = desde.split('-').map(Number);
  const [a2, m2, d2] = hasta.split('-').map(Number);
  if (!a1 || !m1 || !d1) throw new Error(`mesesEntreISO: fecha inválida «${desde}» (se espera AAAA-MM-DD)`);
  if (!a2 || !m2 || !d2) throw new Error(`mesesEntreISO: fecha inválida «${hasta}» (se espera AAAA-MM-DD)`);
  const meses = (a2 - a1) * 12 + (m2 - m1);
  if (d2 >= d1) return meses;
  // No llegó al día del ancla. Sólo cuenta como cumplido si el mes destino NO TIENE ese
  // día y `hasta` cayó en su último día — o sea, si es el recorte de `sumarMesesISO`.
  // Si el día existía y no se alcanzó, el mes de verdad no está cumplido.
  return d2 === ultimoDiaDelMes(a2, m2 - 1) && d1 > d2 ? meses : meses - 1;
}


// ═══════════════════════════════════════════════════════════════════════════════════
// 🕐 EL RELOJ DE MÉRIDA — el motor de la hora (BUG-3874 · 6-oct)
// ═══════════════════════════════════════════════════════════════════════════════════
// Antes vivía en DOS lugares con dos implementaciones (`backend/src/helpers/timezone.ts` y `frontend/src/utils/dateFormatters.ts`, las dos con `date-fns-tz`) y
// en ~6 copias sueltas del «-6 horas». Ahora UNA, sin dependencias (sólo `Intl`, que traen Node y el navegador): el paquete compartido no depende de nada.
//
// El proceso del servidor corre con TZ=UTC y la sesión de MySQL en UTC: de las 18:00 a las 24:00 de Mérida ya es «mañana» ahí. Todo «hoy» de negocio sale de AQUÍ.

/** La zona horaria del negocio. La usan también los crons (`node-cron`) — una sola constante. */
export const ZONA_HORARIA_NEGOCIO = 'America/Merida';

const FORMATO_PARED_MERIDA = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONA_HORARIA_NEGOCIO, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

/** Los componentes de la hora de pared de Mérida en el instante `ahora`. */
function paredEnMerida(ahora: Date): { anio: number; mes: number; dia: number; hora: number; minuto: number; segundo: number } {
  const v: Record<string, number> = {};
  for (const p of FORMATO_PARED_MERIDA.formatToParts(ahora)) if (p.type !== 'literal') v[p.type] = Number(p.value);
  return { anio: v.year!, mes: v.month!, dia: v.day!, hora: v.hour!, minuto: v.minute!, segundo: v.second! };
}

/**
 * La fecha/hora de `ahora` (default: el reloj real) expresada como la hora de PARED de Mérida — léela con los getters LOCALES del objeto devuelto
 * (`getFullYear`, `getMonth`, `getDate`…): se construye con el constructor local, así que no depende de la zona del proceso (salvo el salto de horario de verano de la zona local del navegador).
 * ⚠️ NUNCA `.toISOString()` sobre su resultado: vuelve a convertir a UTC y deshace el ajuste. Para el día usa `hoyEnMeridaISO()`.
 * También sirve para mostrar un INSTANTE en hora de Mérida (`hoyEnMerida(unInstante)`).
 */
export function hoyEnMerida(ahora: Date = new Date()): Date {
  if (Number.isNaN(ahora.getTime())) return new Date(NaN); // instante inválido ⇒ fecha inválida, como el reloj de antes (no lanza)
  const p = paredEnMerida(ahora);
  return new Date(p.anio, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo, ahora.getMilliseconds());
}

/** El día de hoy en Mérida como `AAAA-MM-DD` — reemplazo de `new Date().toISOString().slice(0, 10)` (que da el día de UTC). */
export function hoyEnMeridaISO(ahora: Date = new Date()): string {
  if (Number.isNaN(ahora.getTime())) return 'NaN-NaN-NaN'; // instante inválido: igual que el reloj de antes (no lanza); quien lo recibe ya valida el formato
  const p = paredEnMerida(ahora);
  return `${p.anio}-${String(p.mes).padStart(2, '0')}-${String(p.dia).padStart(2, '0')}`;
}

/**
 * Diferencia en DÍAS CALENDARIO entre dos fechas `AAAA-MM-DD` (`hastaISO - desdeISO`). Aritmética pura de calendario (no hay zona que resolver):
 * `diasEntreISO('2026-09-02', '2026-09-05')` → 3 (vence en 3 días) · `('2026-09-02','2026-09-01')` → -1 (vencido desde ayer) · mismo día → 0 (vence HOY: no es «vencido»).
 * Comparar vencido/no-vencido NUNCA se hace restando milisegundos entre un instante y una columna DATE (I-12, D-2026-09-01).
 */
export function diasEntreISO(desdeISO: string, hastaISO: string): number {
  const [ay, am, ad] = desdeISO.split('-').map(Number);
  const [by, bm, bd] = hastaISO.split('-').map(Number);
  return Math.round((Date.UTC(by!, bm! - 1, bd!) - Date.UTC(ay!, am! - 1, ad!)) / 86_400_000);
}

/**
 * `iso` (AAAA-MM-DD) ± `dias` días de CALENDARIO — el dueño de «hace N días / dentro de N días» sobre columnas DATE: se le pasa `hoyEnMeridaISO()`,
 * NUNCA `NOW()`/`DATE_SUB(NOW(), …)` de MySQL. Mediodía UTC: ninguna zona la mueve de día.
 */
export function sumarDiasISO(iso: string, dias: number): string {
  const [anioTxt, mesTxt, diaTxt] = iso.split('-');
  const f = new Date(Date.UTC(Number(anioTxt), Number(mesTxt) - 1, Number(diaTxt), 12, 0, 0));
  f.setUTCDate(f.getUTCDate() + dias);
  return f.toISOString().slice(0, 10);
}

/**
 * El desfase de Mérida respecto de UTC en el instante `ahora`, como `±HH:MM` (hoy `-06:00`, sin horario de verano), derivado del mismo `Intl` que contesta `hoyEnMeridaISO`:
 * UNA sola respuesta a «¿cuánto va Mérida de UTC?», en vez de un `-6 horas` escrito a mano en cada consulta (BUG-3879).
 */
export function desfaseMeridaUTC(ahora: Date = new Date()): string {
  const p = paredEnMerida(ahora);
  const minutos = Math.round((Date.UTC(p.anio, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo) - Math.floor(ahora.getTime() / 1000) * 1000) / 60_000);
  const signo = minutos < 0 ? '-' : '+';
  const abs = Math.abs(minutos);
  return `${signo}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

/**
 * El DÍA DE NEGOCIO de una columna `TIMESTAMP`, como TEXTO SQL (BUG-3879): `DATE(CONVERT_TZ(columna, '+00:00', '-06:00'))`. La sesión de MySQL corre en UTC, así que `DATE(columna)` lee el día de UTC:
 * de las 18:00 a las 24:00 de Mérida ya es «mañana» ahí. Es el ÚNICO lugar donde se escribe el `CONVERT_TZ` (candado T6.1 de la bodega); el backend lo envuelve con `sql.raw` en `diaDeNegocioSQL`
 * (`helpers/dia-de-negocio-sql.ts`). `columna` es un nombre de columna con alias opcional (`ir.timestamp_creacion`), nunca texto del usuario: se valida.
 */
export function diaDeNegocioSQLTexto(columna: string): string {
  if (!/^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)?$/.test(columna)) {
    throw new Error(`diaDeNegocioSQLTexto: «${columna}» no es un nombre de columna`);
  }
  return `DATE(CONVERT_TZ(${columna}, '+00:00', '${desfaseMeridaUTC()}'))`;
}

/** El INSTANTE UTC real que corresponde a una hora de pared de Mérida (inversa exacta de `hoyEnMerida`; resuelve cualquier cambio de horario histórico). */
function instanteDeParedEnMerida(anio: number, mes: number, dia: number, hora = 0, minuto = 0, segundo = 0): Date {
  const aprox = Date.UTC(anio, mes - 1, dia, hora, minuto, segundo);
  const desfase = (ms: number): number => {
    const p = paredEnMerida(new Date(ms));
    return Date.UTC(p.anio, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo) - Math.floor(ms / 1000) * 1000;
  };
  const primero = aprox - desfase(aprox);
  return new Date(aprox - desfase(primero));
}

/**
 * Rango [desde, hasta) del año calendario `anio` en hora de Mérida, como INSTANTES UTC reales — para comparar contra columnas `timestamp()` sin pasar por `YEAR(columna)` de
 * MySQL (que lee la zona de la SESIÓN). Etiqueta y filtro de conteo de un folio se mueven juntos (I-16): `2026-01-01 00:00 Mérida` → `2026-01-01T06:00:00.000Z`.
 */
export function rangoAnioMeridaUTC(anio: number): { desde: Date; hasta: Date } {
  return { desde: instanteDeParedEnMerida(anio, 1, 1), hasta: instanteDeParedEnMerida(anio + 1, 1, 1) };
}

/**
 * Rango [desde, hasta) del DÍA `AAAA-MM-DD` de Mérida, como INSTANTES UTC reales — para comparar contra columnas `timestamp()` sin pasar por `DATE(columna)` de MySQL
 * (que lee la zona de la SESIÓN = UTC): `DATE(creado_en) = hoy` deja fuera lo de las 18:00–24:00 de Mérida y mete lo de las 18:00–24:00 de ayer.
 * `2026-10-06` → `2026-10-06T06:00:00.000Z` a `2026-10-07T06:00:00.000Z`.
 */
export function rangoDiaMeridaUTC(iso: string): { desde: Date; hasta: Date } {
  const [anioTxt, mesTxt, diaTxt] = iso.split('-');
  const manana = sumarDiasISO(iso, 1).split('-');
  return {
    desde: instanteDeParedEnMerida(Number(anioTxt), Number(mesTxt), Number(diaTxt)),
    hasta: instanteDeParedEnMerida(Number(manana[0]), Number(manana[1]), Number(manana[2])),
  };
}

/**
 * La SEMANA de trabajo en Mérida: **lunes a domingo**, como `AAAA-MM-DD` (visto bueno semanal). Se calcula sobre `hoyEnMerida()`, NO sobre `new Date()`: a las 19:00 de Mérida un
 * domingo ya es lunes en UTC. `getDay()` da 0 para domingo; aquí el domingo es el ÚLTIMO día.
 */
export function semanaEnMerida(ahora: Date = new Date()): { inicio: string; fin: string } {
  const hoy = hoyEnMerida(ahora);
  const diaDeLaSemana = hoy.getDay() === 0 ? 7 : hoy.getDay();   // lunes=1 … domingo=7
  const comoISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const lunes = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - (diaDeLaSemana - 1));
  const domingo = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + (7 - diaDeLaSemana));
  return { inicio: comoISO(lunes), fin: comoISO(domingo) };
}

// BUG-3888 (N-3) — un `regex(/^\d{4}-\d{2}-\d{2}$/)` sólo comprueba la FORMA: `2026-02-31` la pasa y MySQL la rechaza con un 500. UN dueño para «¿existe ese día?»
// (antes había otra copia en crm.routes.ts; el cerrador las juntó aquí al integrar el lote 6a).
/** ¿«AAAA-MM-DD» es un día que existe en el calendario? (`2026-02-31` no.) La forma ya la comprobó el `regex`. */
export function esFechaDeCalendario(s: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const [anio, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const f = new Date(Date.UTC(anio, mes - 1, dia));
  return f.getUTCFullYear() === anio && f.getUTCMonth() === mes - 1 && f.getUTCDate() === dia;
}
